import { Link, useParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import DetailActions from "@/components/feature/DetailActions";
import EmptyState from "@/components/feature/EmptyState";
import { movies } from "@/mocks/movies";
import { usePageMeta } from "@/hooks/usePageMeta";
import { formatHeat } from "@/utils/format";

export default function MovieDetail() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((item) => item.id === id);

  usePageMeta(
    movie ? `${movie.title} - 午夜视频高清观看` : "影片不存在 - 午夜视频",
    movie
      ? `${movie.title}（${movie.year}）${movie.genre}片，${movie.director}导演，${movie.cast}主演。${movie.synopsis.slice(0, 60)}`
      : "没有找到这部影片，返回午夜视频电影分类继续浏览其他午夜电影。",
    movie
      ? `${movie.title},${movie.genre}电影,${movie.year}午夜电影,午夜视频,${movie.region}电影`
      : "午夜视频,午夜电影",
  );

  if (!movie) {
    return (
      <PageLayout>
        <div className="w-full px-4 py-10 md:px-8">
          <Breadcrumb items={[{ label: "电影分类", to: "/movies" }, { label: "影片不存在" }]} />
          <EmptyState
            icon="ri-file-warning-line"
            title="这部影片暂时不在片库中"
            description="可能是链接里的编号有误，或者内容已经下架。回到电影分类页，还有更多午夜影片可以挑选。"
            actionLabel="返回电影分类"
            actionTo="/movies"
          />
        </div>
      </PageLayout>
    );
  }

  const related = movies
    .filter((item) => item.id !== movie.id && (item.genre === movie.genre || item.region === movie.region))
    .sort((a, b) => b.heat - a.heat)
    .slice(0, 4);

  const infoRows = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast },
    { label: "类型", value: movie.genre },
    { label: "地区", value: movie.region },
    { label: "上映年份", value: `${movie.year} 年` },
    { label: "片长", value: `${movie.duration} 分钟` },
    { label: "上映日期", value: movie.releaseDate },
    { label: "站内热度", value: formatHeat(movie.heat) },
  ];

  return (
    <PageLayout>
      <div className="w-full px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb items={[{ label: "电影分类", to: "/movies" }, { label: movie.title }]} />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div>
            <div className="flex flex-col gap-6 sm:flex-row">
              <div className="w-full max-w-[220px] shrink-0 sm:w-[200px] md:w-[236px]">
                <div className="relative aspect-[2/3] w-full overflow-hidden rounded-lg border border-background-700 bg-background-800">
                  <img
                    src={movie.poster}
                    alt={`${movie.title} 电影海报大图`}
                    title={`${movie.title} - 午夜视频高清午夜电影`}
                    className="h-full w-full object-cover object-top"
                  />
                  <span className="absolute left-3 top-3 rounded-md bg-accent-500 px-2 py-1 font-label text-[10px] tracking-wider text-background-950">
                    {movie.genre}
                  </span>
                </div>
              </div>

              <div className="min-w-0 flex-1">
                <h1 className="font-heading text-2xl font-bold leading-tight tracking-wide text-foreground-50 md:text-3xl">
                  {movie.title}
                </h1>
                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-foreground-400">
                  <span className="rounded-md bg-background-800 px-2 py-1 text-foreground-200">
                    {movie.year}
                  </span>
                  <span>{movie.region}</span>
                  <span className="h-3 w-px bg-background-700" />
                  <span>{movie.duration} 分钟</span>
                  <span className="h-3 w-px bg-background-700" />
                  <span className="flex items-center gap-1 text-accent-400">
                    <i className="ri-fire-line text-sm" />
                    {formatHeat(movie.heat)} 热度
                  </span>
                </div>

                <p className="mt-4 text-sm leading-7 text-foreground-300">{movie.synopsis}</p>

                <div className="mt-4 flex flex-wrap gap-1.5">
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-secondary-900/60 px-2.5 py-1 text-[11px] text-secondary-200"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="mt-6">
                  <DetailActions type="movie" id={movie.id} />
                </div>
              </div>
            </div>

            <div className="mt-7 flex items-start gap-2.5 rounded-lg border border-accent-500/25 bg-primary-950/70 px-4 py-3.5">
              <i className="ri-information-line mt-0.5 text-base text-accent-500" />
              <p className="text-xs leading-6 text-foreground-400">
                本站暂未接入在线播放功能，当前页面仅展示影片封面、资料与简介。你可以先收藏本片，或查看下方相关推荐继续发现内容。
              </p>
            </div>

            <section className="mt-8">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-50">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                剧情简介
              </h2>
              <p className="mt-4 text-sm leading-8 text-foreground-300">{movie.synopsis}</p>
              <p className="mt-3 text-sm leading-8 text-foreground-400">
                影片以深夜为叙事核心，通过克制的镜头与层次分明的光影，把城市在午夜时分的另一面呈现出来。适合在安静的环境下独自观看，也适合作为深夜片单中的一部过渡之作。
              </p>
            </section>

            <section className="mt-8">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-50">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                影片信息
              </h2>
              <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-0 overflow-hidden rounded-lg border border-background-800 bg-background-900/60 sm:grid-cols-2">
                {infoRows.map((row) => (
                  <div
                    key={row.label}
                    className="flex items-baseline gap-3 border-b border-background-800/70 px-4 py-3 last:border-b-0 sm:last:border-b"
                  >
                    <dt className="w-20 shrink-0 text-xs text-foreground-500">{row.label}</dt>
                    <dd className="min-w-0 text-sm text-foreground-200">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          </div>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg border border-background-800 bg-background-900/70 p-4 md:p-5">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-50">
                  <i className="ri-movie-2-line text-base text-accent-500" />
                  相关推荐
                </h2>
                <Link
                  to="/movies"
                  className="text-xs text-foreground-500 transition-colors hover:text-accent-400"
                >
                  更多
                </Link>
              </div>
              <ul className="mt-4 space-y-3">
                {related.map((item) => (
                  <li key={item.id}>
                    <Link
                      to={`/detail/movie/${item.id}`}
                      className="group flex gap-3 rounded-md p-1.5 transition-colors hover:bg-background-800/70"
                    >
                      <div className="h-[76px] w-[54px] shrink-0 overflow-hidden rounded-md border border-background-700 bg-background-800">
                        <img
                          src={item.poster}
                          alt={`${item.title} 海报`}
                          title={`${item.title} - 午夜视频相关推荐`}
                          className="h-full w-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0 flex-1 py-0.5">
                        <h3 className="truncate text-sm text-foreground-100 transition-colors group-hover:text-accent-400">
                          {item.title}
                        </h3>
                        <p className="mt-1 truncate text-[11px] text-foreground-500">
                          {item.year} · {item.genre} · {item.region}
                        </p>
                        <p className="mt-1 flex items-center gap-1 text-[11px] text-secondary-300">
                          <i className="ri-fire-line text-xs text-accent-500" />
                          {formatHeat(item.heat)}
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-4 rounded-lg border border-background-800 bg-primary-950 p-4 md:p-5">
              <h2 className="font-heading text-sm font-semibold text-foreground-100">片段速览</h2>
              <p className="mt-2 text-xs leading-6 text-foreground-400">
                想先感受氛围？可以先去短视频分类看看同类型的深夜片段，再决定今晚的片单顺序。
              </p>
              <Link
                to="/short-videos"
                className="mt-3 inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-accent-400 transition-colors hover:text-accent-300"
              >
                浏览短视频
                <i className="ri-arrow-right-line text-sm" />
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </PageLayout>
  );
}